/**
 * DesignTab — the CAD program (DWG/DXF reader + editor) as its own tab.
 *
 * Completely separate from the Estimate tab: no job table, no analytics.
 * Landing = drawings grid + "New drawing" / "Open DWG·DXF"; tapping a
 * drawing goes straight into the CAD canvas (WorkspaceView) with the tool
 * rail, layers panel and DXF-saved edits. Design jobs are flagged
 * `design: true` on the server so they never appear in Estimate.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, FilePlus2, FolderOpen, Layers, Loader2, RefreshCw, Send, PencilRuler, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { TakeoffProvider, useTakeoff } from "./store";
import WorkspaceView from "./WorkspaceView";
import { listDesignDrawings, createBlankCad, designToEstimate, cadPrepare, deleteDrawing } from "./api";

const FONT = { fontFamily: "'Manrope', 'IBM Plex Sans', system-ui, sans-serif" };
const UNITS = [["ft", "Feet"], ["in", "Inches"], ["m", "Metres"], ["mm", "Millimetres"]];

function DrawingCard({ d, onOpen, onSend, onRetry, onDelete, busy }) {
  const ready = d.status === "ready";
  const working = d.status === "processing" || d.status === "rendering" || d.status === "pending";
  const failed = d.status === "error" || d.status === "stalled";
  return (
    <div className="group flex flex-col rounded-xl border border-white/10 bg-[#14171c] p-4 text-left shadow-lg transition-colors hover:border-[#f97316]/60" data-testid={`design-card-${d.drawing_id}`}>
      <button onClick={() => onOpen(d)} className="flex flex-1 flex-col text-left" data-testid={`design-open-drawing-${d.drawing_id}`}>
        <div className="mb-3 flex h-24 flex-col items-center justify-center gap-2 rounded-lg bg-[#0b0d10] px-3 text-white/20 group-hover:text-[#f97316]/70">
          {ready ? <Layers className="h-9 w-9" />
            : failed ? <span className="text-center text-[11px] leading-snug text-red-400" data-testid={`design-card-error-${d.drawing_id}`}>{d.error || "Could not read this file"}</span>
              : <>
                <Loader2 className="h-6 w-6 animate-spin text-[#f97316]" />
                <div className="h-1 w-full overflow-hidden rounded-full bg-white/10"><div className="h-full bg-[#f97316] transition-[width] duration-700" style={{ width: `${Math.max(3, d.progress?.pct || 3)}%` }} /></div>
                <span className="truncate font-mono text-[9px] uppercase tracking-widest text-white/50">{d.progress?.detail || "Reading the file…"}</span>
              </>}
        </div>
        <div className="truncate text-sm font-semibold text-white" title={d.name}>{d.name}</div>
        <div className="mt-0.5 truncate font-mono text-[10px] uppercase tracking-widest text-white/40">{d.project_name}</div>
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 font-mono text-[10px] text-white/55">
          {ready ? <><span>{d.sheets} sheet{d.sheets === 1 ? "" : "s"}</span><span>{d.layers} layers</span></> : <span className={failed ? "text-red-400" : "text-[#f97316]"}>{d.status === "stalled" ? "stalled" : failed ? "error" : `${d.progress?.pct || 0}% · processing…`}</span>}
          {d.owner && <span className="ml-auto truncate text-white/40">{d.owner}</span>}
        </div>
      </button>
      <div className="mt-3 grid grid-cols-2 gap-1.5">
        {ready ? (
          <button onClick={() => onSend(d)} disabled={busy} title="Create an Estimate job with this drawing's sheets attached"
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-white/10 px-2 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-white/60 hover:border-[#f97316] hover:text-[#f97316] disabled:opacity-30"
            data-testid={`design-send-estimate-${d.drawing_id}`}>
            <Send className="h-3 w-3" /> To Estimate
          </button>
        ) : (
          <button onClick={() => onRetry(d)} disabled={busy} title={working ? "Restart the conversion from scratch" : "Try converting again"}
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-white/10 px-2 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-white/60 hover:border-[#f97316] hover:text-[#f97316] disabled:opacity-30"
            data-testid={`design-retry-${d.drawing_id}`}>
            <RefreshCw className="h-3 w-3" /> {working ? "Restart" : "Retry"}
          </button>
        )}
        <button onClick={() => onDelete(d)} disabled={busy} title={working ? "Cancel and delete this drawing" : "Delete this drawing"}
          className="inline-flex items-center justify-center gap-1.5 rounded-md border border-white/10 px-2 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-white/60 hover:border-red-500 hover:text-red-400 disabled:opacity-30"
          data-testid={`design-delete-${d.drawing_id}`}>
          <Trash2 className="h-3 w-3" /> {working ? "Cancel" : "Delete"}
        </button>
      </div>
    </div>
  );
}

function DesignHome({ onOpenDrawing, onNewDrawing, onOpenFile }) {
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(false);
  const [units, setUnits] = useState("ft");
  const fileRef = useRef(null);
  const load = useCallback(() => listDesignDrawings().then((r) => setList(r.drawings)).catch(() => setList([])), []);
  useEffect(() => { load(); }, [load]);
  // keep refreshing while something is still converting
  useEffect(() => {
    if (!list?.some((d) => !["ready", "error", "stalled"].includes(d.status))) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [list, load]);

  const retry = async (d) => {
    setBusy(true);
    try { await cadPrepare(d.drawing_id, true); toast.message(`Converting ${d.name} again…`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not restart the conversion"); } finally { setBusy(false); }
  };
  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.name}"? This removes the drawing, its sheets and any CAD edits.`)) return;
    setBusy(true);
    try { await deleteDrawing(d.drawing_id); toast.success(`"${d.name}" deleted`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete the drawing"); } finally { setBusy(false); }
  };
  const send = async (d) => {
    setBusy(true);
    try {
      const r = await designToEstimate(d.project_id);
      toast.success(`Estimate job "${r.project.name}" created — ${r.drawings} sheet${r.drawings === 1 ? "" : "s"} attached. It's waiting in the Estimate tab.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send to estimate"); } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6" data-testid="design-home">
      <div className="mb-6 flex flex-wrap items-end gap-3">
        <div className="mr-auto">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#f97316]">Design · CAD</div>
          <h2 className="mt-1 text-xl font-bold text-white sm:text-2xl">Drawings</h2>
          <p className="mt-1 text-xs text-white/50">Open a DWG/DXF or start a blank drawing. Layers, sheets and colours are preserved; edits save into a DXF copy.</p>
        </div>
        <label className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-white/50">Units
          <select value={units} onChange={(e) => setUnits(e.target.value)} className="rounded-md border border-white/15 bg-[#14171c] px-2 py-1.5 text-xs text-white" data-testid="design-units">
            {UNITS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <button onClick={() => onNewDrawing(units)} className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-4 py-2 text-xs font-bold uppercase tracking-widest text-white hover:border-[#f97316] hover:text-[#f97316]" data-testid="design-new-drawing">
          <FilePlus2 className="h-4 w-4" /> New drawing
        </button>
        <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 rounded-lg bg-[#f97316] px-4 py-2 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#ea580c]" data-testid="design-open-file">
          <FolderOpen className="h-4 w-4" /> Open DWG · DXF
        </button>
        <input ref={fileRef} type="file" accept=".dwg,.dxf" className="hidden" data-testid="design-file-input"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onOpenFile(f); }} />
        <button onClick={load} className="rounded-lg border border-white/10 p-2 text-white/50 hover:text-white" title="Refresh" data-testid="design-refresh"><RefreshCw className="h-4 w-4" /></button>
      </div>

      {list === null ? (
        <div className="flex items-center gap-2 py-16 text-sm text-white/50"><Loader2 className="h-4 w-4 animate-spin" /> Loading drawings…</div>
      ) : !list.length ? (
        <div className="rounded-2xl border border-dashed border-white/15 px-6 py-16 text-center" data-testid="design-empty">
          <PencilRuler className="mx-auto h-10 w-10 text-white/20" />
          <p className="mt-3 text-sm text-white/60">No drawings yet. Open a DWG/DXF from your device or start a blank one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="design-drawing-grid">
          {list.map((d) => <DrawingCard key={d.drawing_id} d={d} onOpen={onOpenDrawing} onSend={send} onRetry={retry} onDelete={remove} busy={busy} />)}
        </div>
      )}
    </div>
  );
}

function DesignShell() {
  const { activeId, setActiveId, activeProject, createProject, uploadFiles, wsError, loadWorkspace } = useTakeoff();
  const [openDrawingId, setOpenDrawingId] = useState(null);
  const [ctxLine, setCtxLine] = useState("");
  const pendingFile = useRef(null); // {pid, file} — uploaded once the job is active

  // Design tab always starts on the drawings grid, never on a remembered job.
  const started = useRef(false);
  useEffect(() => { if (!started.current) { started.current = true; setActiveId(""); } }, [setActiveId]);

  useEffect(() => {
    const p = pendingFile.current;
    if (p && activeId === p.pid) { pendingFile.current = null; uploadFiles([p.file]); }
  }, [activeId, uploadFiles]);

  const openDrawing = (d) => { setOpenDrawingId(d.drawing_id); setActiveId(d.project_id); };
  const back = () => { setActiveId(""); setOpenDrawingId(null); loadWorkspace(); };

  const newDrawing = async (units) => {
    const name = window.prompt("Name the new drawing:");
    if (!name?.trim()) return;
    try {
      const proj = await createProject({ name: name.trim() });
      const d = await createBlankCad(proj.project_id, { name: name.trim(), units });
      toast.success(`"${d.name}" created — opening…`);
      openDrawing({ drawing_id: d.drawing_id, project_id: proj.project_id });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create the drawing"); }
  };
  const openFile = async (file) => {
    try {
      const proj = await createProject({ name: file.name.replace(/\.(dwg|dxf)$/i, "") });
      pendingFile.current = { pid: proj.project_id, file };
      setActiveId(proj.project_id);
      toast.message(`Reading ${file.name}… layers and sheets will appear as soon as it's converted.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open the file"); }
  };

  if (wsError) {
    return <div className="p-8 text-sm text-red-400" data-testid="design-error">{wsError}</div>;
  }

  return (
    <div className="flex min-h-full flex-col bg-[#0b0d10] text-white" style={FONT} data-testid="design-module">
      {activeId ? (
        <>
          <div className="flex shrink-0 items-center gap-3 border-b border-white/10 bg-[#14171c] px-3 py-2">
            <button onClick={back} className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-2.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-white/70 hover:border-[#f97316] hover:text-[#f97316]" data-testid="design-back">
              <ArrowLeft className="h-3.5 w-3.5" /> Drawings
            </button>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#f97316]">Design · CAD</span>
            <span className="truncate text-sm font-semibold" data-testid="design-context">{[activeProject?.name, ctxLine].filter(Boolean).join(" · ")}</span>
          </div>
          <div className="min-h-0 flex-1"><WorkspaceView onContext={setCtxLine} initialDrawingId={openDrawingId} /></div>
        </>
      ) : (
        <DesignHome onOpenDrawing={openDrawing} onNewDrawing={newDrawing} onOpenFile={openFile} />
      )}
    </div>
  );
}

export default function DesignTab() {
  return (
    <TakeoffProvider design>
      <DesignShell />
    </TakeoffProvider>
  );
}
